import { ColumnDef } from '@tanstack/react-table'
import { UsageCostLabelsMap } from '../../../hooks'
import { formatDateCell } from '../../common/Table/utils'

export const getColumns = (
    labelsMap: UsageCostLabelsMap,
    dataSets: Array<string>
): ColumnDef<unknown>[] => {
    const columns: ColumnDef<unknown>[] = [
        {
            id: 'date',
            accessorKey: 'date',
            header: 'Date',
            sortingFn: 'datetime',
            cell: formatDateCell,
        },
        {
            header: labelsMap['usage'],
            id: 'usage',
            columns: [
                {
                    id: 'usage_commodityUsage',
                    accessorKey: 'usage_commodityUsage',
                    header: labelsMap['usage_commodityUsage'],
                },
                {
                    id: 'usage_baselineUsage',
                    accessorKey: 'usage_baselineUsage',
                    header: labelsMap['usage_baselineUsage'],
                },
                {
                    id: 'usage_budget',
                    accessorKey: 'usage_budget',
                    header: labelsMap['usage_budget'],
                },
                {
                    id: 'usage_transportUsage',
                    accessorKey: 'usage_transportUsage',
                    header: labelsMap['usage_transportUsage'],
                },
                {
                    id: 'usage_weatherNormalized',
                    accessorKey: 'usage_weatherNormalized',
                    header: labelsMap['usage_weatherNormalized'],
                },
            ],
        },
        {
            header: labelsMap['cost'],
            id: 'cost',
            columns: [
                {
                    id: 'cost_totalInvoiced_parent',
                    header: labelsMap['cost_totalInvoiced_noUnit'],
                    columns: [
                        {
                            id: 'cost_totalInvoiced',
                            accessorKey: 'cost_totalInvoiced',
                            header: labelsMap['cost_totalInvoiced'],
                        },
                        {
                            id: 'cost_commodity',
                            header: labelsMap['cost_commodity'],
                            columns: [
                                {
                                    id: 'cost_commodity_totalCommodityCost',
                                    accessorKey:
                                        'cost_commodity_totalCommodityCost',
                                    header: labelsMap[
                                        'cost_commodity_totalCommodityCost'
                                    ],
                                },
                                {
                                    id: 'cost_commodity_supply',
                                    accessorKey: 'cost_commodity_supply',
                                    header: labelsMap['cost_commodity_supply'],
                                },
                                {
                                    id: 'cost_commodity_transmission',
                                    accessorKey: 'cost_commodity_transmission',
                                    header: labelsMap[
                                        'cost_commodity_transmission'
                                    ],
                                },
                                {
                                    id: 'cost_commodity_capacity',
                                    accessorKey: 'cost_commodity_capacity',
                                    header: labelsMap[
                                        'cost_commodity_capacity'
                                    ],
                                },
                            ],
                        },
                        {
                            header: labelsMap[
                                'cost_transport_totalTransportCost'
                            ],
                            id: 'cost_transport_totalTransportCost',
                            columns: [
                                {
                                    id: 'cost_transport_totalTransportCost',
                                    accessorKey:
                                        'cost_transport_totalTransportCost',
                                    header: labelsMap[
                                        'cost_transport_totalTransportCost'
                                    ],
                                },
                                {
                                    id: 'cost_transport_tariff',
                                    accessorKey: 'cost_transport_tariff',
                                    header: labelsMap['cost_transport_tariff'],
                                },
                                {
                                    id: 'cost_transport_4cp4ncp',
                                    accessorKey: 'cost_transport_4cp4ncp',
                                    header: labelsMap['cost_transport_4cp4ncp'],
                                },
                                {
                                    id: 'cost_transport_billingDemand',
                                    accessorKey: 'cost_transport_billingDemand',
                                    header: labelsMap[
                                        'cost_transport_billingDemand'
                                    ],
                                },
                            ],
                        },
                        {
                            id: 'cost_taxes',
                            accessorKey: 'cost_taxes',
                            header: labelsMap['cost_taxes'],
                        },
                        {
                            id: 'cost_budget',
                            accessorKey: 'cost_budget',
                            header: labelsMap['cost_budget'],
                        },
                    ],
                },
                {
                    id: 'cost_baselineCost',
                    accessorKey: 'cost_baselineCost',
                    header: labelsMap['cost_baselineCost'],
                },
                {
                    id: 'cost_other',
                    accessorKey: 'cost_other',
                    header: labelsMap['cost_other'],
                },
            ],
        },
        {
            header: labelsMap['demand'],
            id: 'demand',
            columns: [
                {
                    id: 'demand_actual',
                    accessorKey: 'demand_actual',
                    header: labelsMap['demand_actual'],
                },
                {
                    id: 'demand_billing',
                    accessorKey: 'demand_billing',
                    header: labelsMap['demand_billing'],
                },
            ],
        },
    ]

    return filterOutDataSets(columns, dataSets)
}

const filterOutDataSets = (
    columns: ColumnDef<unknown>[],
    dataSets: Array<string>
): ColumnDef<unknown>[] => {
    const newColumns: ColumnDef<unknown>[] = []

    columns.forEach((column) => {
        // Ensure the `columns` property is typed correctly
        const columnWithSubColumns: ColumnDef<unknown> & {
            columns?: ColumnDef<unknown>[]
        } = column

        if (columnWithSubColumns.columns?.length) {
            const filteredColumns = filterOutDataSets(
                columnWithSubColumns.columns,
                dataSets
            )

            if (filteredColumns.length) {
                newColumns.push({
                    ...column,
                    columns: filteredColumns,
                })
            }
        } else if (dataSets.includes(column.id as string)) {
            newColumns.push(column)
        }
    })

    return newColumns
}
